import { Info, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  FilterDropdown,
  GenericTable,
  Pagination,
  RoleValidator,
  SearchableDropdown,
} from "../../../Components/Common";
import {
  getRNStatusColor,
  RETURN_NOTE_COLUMNS,
  RETURN_NOTE_COLUMNS_LABELS,
  RETURN_NOTE_STATUS,
  RETURN_NOTE_STATUS_LABELS,
  RN_STATUS_OPTIONS,
} from "../constants";
import { ROLES } from "../../../Constants/Strings";
import { formatDateTime } from "../../../Utils";
import { useNavigate } from "react-router-dom";
import {
  ADD_RETURN_NOTE,
  DETAIL_RETURN_NOTE,
  EDIT_RETURN_NOTE,
} from "../../../routes";
import { MdModeEditOutline } from "react-icons/md";
import { useReturnNote } from "../Hooks/useReturnNote";
import { useWarehouse } from "../Hooks/useWarehouse";

const ReturnNoteList = () => {
  const {
    loading: rnLoading,
    filters,
    page,
    onPageChange,
    onPageSizeChange,
    totalPages,
    rowsPerPage,
    setFilters,
    fetchReturnNotes,
    deleteReturnNoteById,
  } = useReturnNote();
  const { fetchWarehouses } = useWarehouse();
 
  const [returnNotes, setReturnNotes] = useState([]);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    const data = await fetchReturnNotes();
    if (data) setReturnNotes(data);
  };

  const deleteNote = async (rn_id) => {
    await deleteReturnNoteById(rn_id);
    await fetchData();
  };

  const renderField = (column, row) => {
    switch (column) {
      case "warehouse_name":
        return row.warehouse_name || "N/A";
      case "status":
        return (
          <div
            className={`py-1 px-2 font-semibold w-fit ${getRNStatusColor(
              row.status
            )}  rounded-md`}
          >
            {RETURN_NOTE_STATUS_LABELS[row.status]}
          </div>
        );
      case "created":
        return formatDateTime(row.created) || "N/A";
      case "actions":
        return (
          <div className="flex gap-4">
            <button
              className="p-1 rounded-lg transition-colors"
              onClick={() =>
                navigate(DETAIL_RETURN_NOTE.replace(":instanceId", row.id))
              }
            >
              <Info className="h-5 w-5 text-primary" />
            </button>
            <RoleValidator
                    allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                    mode="disable"
                >
            <button
              onClick={() =>
                navigate(EDIT_RETURN_NOTE.replace(":instanceId", row.id))
              }
              disabled={row.status !== RETURN_NOTE_STATUS.DRAFT}
              className={`p-1 rounded-lg transition-colors
                        ${
                          row.status === RETURN_NOTE_STATUS.DRAFT
                            ? "cursor-pointer"
                            : "cursor-not-allowed"
                        }`}
            >
              <MdModeEditOutline
                className={`h-5 w-5 ${
                  row.status === RETURN_NOTE_STATUS.DRAFT
                    ? "text-blue-500"
                    : "text-gray-400"
                }`}
              />
            </button>
            </RoleValidator>
            <RoleValidator
                    allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                    mode="disable"
                >
            <button
              onClick={() => deleteNote(row.id)}
              disabled={row.status !== RETURN_NOTE_STATUS.DRAFT}
              className={`p-1 rounded-lg transition-colors
                        ${
                          row.status === RETURN_NOTE_STATUS.DRAFT
                            ? "cursor-pointer"
                            : "cursor-not-allowed"
                        }`}
            >
              <Trash2
                className={`h-5 w-5 ${
                  row.status === RETURN_NOTE_STATUS.DRAFT
                    ? "text-red-500"
                    : "text-gray-400"
                }`}
              />
              </button>
            </RoleValidator>
          </div>
        );
      default:
        return row[column] || "N/A";
    }
  };

  const renderMobileCard = (row) => (
    <div
      className="mt-3 px-1"
      onClick={() =>
        navigate(DETAIL_RETURN_NOTE.replace(":instanceId", row.id))
      }
    >
      <div className="bg-white shadow-lg rounded-2xl border border-gray-200 overflow-hidden relative">
        <div className="p-4 flex flex-col space-y-2 w-[70%]">
          <div className="text-lg font-semibold text-gray-800 truncate">
            {row.rn_code || "N/A"}
          </div>
          <div className="flex gap-2 text-gray-600 text-sm truncate">
            <div>{row.warehouse_name || "N/A"}</div>
          </div>
        </div>

        <div className="absolute top-3 right-3 flex space-x-3">
          <RoleValidator
                    allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                    mode="disable"
                >
          <button
            onClick={() =>
              navigate(EDIT_RETURN_NOTE.replace(":instanceId", row.id))
            }
            className="p-1 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
            disabled={row.status !== RETURN_NOTE_STATUS.DRAFT}
          >
            <MdModeEditOutline className="h-4 w-4 text-blue-500" />
            </button>
          </RoleValidator>
          <RoleValidator
                    allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
                    mode="disable"
                >
          <button
            className="p-1 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
            onClick={() => deleteNote(row.id)}
            disabled={row.status !== RETURN_NOTE_STATUS.DRAFT}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </RoleValidator>
        </div>
        <div
          className={`absolute bottom-3 right-4 px-3 py-1 rounded-full text-sm font-medium shadow-inner ${getRNStatusColor(
            row.status
          )}`}
        >
          {RETURN_NOTE_STATUS_LABELS[row.status]}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    fetchData();
  }, [filters, page, totalPages, rowsPerPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };

    if (isStatusFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isStatusFilterOpen]);

  return (
    <div className="mt-4 px-4 max-h-full overflow-y-hidden">
      <div className="flex justify-between items-center mb-1">
        <h1 className="text-2xl font-semibold text-gray-800">
          Return Notes
        </h1>
        <RoleValidator allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}>
          <button
            className="flex gap-1 p-2 bg-primary text-white rounded hover:bg-gray-700 transition"
            onClick={() => navigate(ADD_RETURN_NOTE)}
          >
            <Plus />
            <span className="hidden md:block">Add Return Note</span>
          </button>
        </RoleValidator>
      </div>
      <div className="bg-white w-full rounded-md shadow-md border px-2 py-1">
        <div className="flex gap-2">
          <div>
            <SearchableDropdown
              fetchFunction={(param) =>
                fetchWarehouses({ ...param, is_site: true })
              }
              onSelect={(val) => setFilters({ ...filters, warehouse: val })}
              valueKey="id"
              labelKey="name"
              includeAllOption
              allOptionLabel="All Warehouse"
              placeholder={"Select Warehouse"}
              width="64"
            />
          </div>
          <div>
          <SearchableDropdown
              fetchFunction={async (params) => {
                const res = await fetchReturnNotes(params);
                const uniqueStatuses = [...new Set(res.map((r) => r.status))];
                return uniqueStatuses.map((s) => ({ id: s, status: s }));
              }}
            onSelect={(val) => setFilters({ ...filters, status: val })}
            valueKey="id"
              labelKey="status"
              includeAllOption
              allOptionLabel="All Status"
            placeholder={"Select Status"}
            />
          </div>
        </div>
      </div>
      <div className="py-2">
        <div className="h-[57vh] overflow-y-auto">
          {rnLoading ? (
            <div className="h-full w-full flex justify-center items-center">
              <div className="animate-spin border-t h-7 w-7 rounded-full border-blue-600"></div>
            </div>
          ) : (
            <GenericTable
              tableColumns={RETURN_NOTE_COLUMNS}
              tableRows={returnNotes}
              columnLabels={RETURN_NOTE_COLUMNS_LABELS}
              renderField={renderField}
              renderMobileCard={renderMobileCard}
            />
          )}
        </div>
        <div className="shrink-0 mt-4">
          <Pagination
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            totalPages={totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default ReturnNoteList;
